import React, { useCallback, Component, Suspense, lazy, type ReactNode } from "react";
import Icons from "./Icons";
import MoreOptionTabButton from "@/components/MoreOptionTabButton";
import PaymentMethods from "./PaymentMethods";
import type { PaymentMethodsProps, GameData, PaymentMethodDefinitionsResponse } from "@/interfaces";
import { Skeleton } from "@/components/ui/skeleton";
import { useStyleTheme } from "@/hooks/useStyleTheme";
import { useAnalytics } from "@/hooks/useAnalytics";

// Lazy load MoreOption to catch import errors
const MoreOption = lazy(() => import("@/components/paymentMethods/MoreOption"));

// Check for debug mode
const DEBUG_ENABLED = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

// Error Boundary to catch crashes when rendering MoreOption
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class MoreOptionErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[MoreOptionErrorBoundary] CRASHED:', error);
    console.error('[MoreOptionErrorBoundary] Component stack:', errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="px-5 py-4">
          <div style={{
            background: '#fee2e2',
            border: '1px solid #f87171',
            borderRadius: '8px',
            padding: '12px',
            color: '#991b1b',
            fontSize: '12px',
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ⚠️ Payment Options Error
            </div>
            <div style={{ marginBottom: '8px' }}>
              Some payment options could not be loaded in this browser.
            </div>
            {DEBUG_ENABLED && (
              <div style={{
                background: '#1f2937',
                color: '#f87171',
                padding: '8px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '10px',
                marginTop: '8px',
                maxHeight: '150px',
                overflow: 'auto',
                wordBreak: 'break-all',
              }}>
                <div>Error: {this.state.error?.message}</div>
                <div>Name: {this.state.error?.name}</div>
                {this.state.error?.stack && (
                  <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                    {this.state.error.stack}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const PaymentMethodsList = ({
  showNav,
  setShowNav,
  savedCards,
  email,
  setEmail,
  emailError,
  setEmailError,
  onConfirm,
  showApplePay,
  showGooglePay,
  clientSecret,
  activeButton,
  setActiveButton,
  isSkeletonPhase = false,
  hasExpressLoadedOnce,
  onExpressLoaded,
  isExpressReinitializing,
  onExpressReinitialized,
  gameData,
  sessionId,
  paypalEnabled = true,
  paymentMethodDefinitions,
  optimisticGpay = false,
}: PaymentMethodsProps & {
  activeButton: "Credit Card" | "One Click" | "More Options";
  setActiveButton: (button: "Credit Card" | "One Click" | "More Options") => void;
  gameData: GameData;
  sessionId: string;
  paypalEnabled?: boolean;
  paymentMethodDefinitions?: PaymentMethodDefinitionsResponse | null;
  optimisticGpay?: boolean;
}) => {
  const theme = useStyleTheme(gameData);
  const { track } = useAnalytics();
  
  const handleButtonClick = useCallback((title: "Credit Card" | "One Click" | "More Options") => {
    if (title === activeButton) return;
    
    // Track click event
    try {
      const ctaNameMap: Record<string, string> = {
        "Credit Card": "card",
        "One Click": "one click",
        "More Options": "more options",
      };
      track('click_in_checkout', { cta_name: ctaNameMap[title] || title.toLowerCase() });
    } catch {
      // Ignore tracking errors
    }
    
    // If switching to One Click tab and Express has loaded before, trigger reinitializing state
    if (title === "One Click" && hasExpressLoadedOnce) {
      onExpressReinitialized?.(); // This will be called from CheckoutForm to set isExpressReinitializing to true
    }
    
    setActiveButton(title);
  }, [activeButton, setActiveButton, hasExpressLoadedOnce, onExpressReinitialized, track]);

  // Theme-based styles
  const tabContainerStyle = {
    borderColor: theme.colors.primary,
  };
  
  const getActiveTabStyle = (isActive: boolean) => ({
    backgroundColor: isActive ? theme.colors.activeTabBg : 'transparent',
  });

  return (
    <section className="flex flex-col flex-1">
      {showNav && <div 
        className="flex justify-between gap-2 border-[1px] rounded-[10px] p-1 mb-[14px] mx-5"
        style={tabContainerStyle}
      >
        <div
          className="flex-1 rounded-lg px-3 transition-all duration-200 flex items-center justify-center"
          style={getActiveTabStyle(activeButton === "One Click")}
        >
          <MoreOptionTabButton
            title="One Click"
            icon={
              activeButton === "One Click" ? (
                <Icons type="boltActive" size={27} color={theme.colors.primary} />
              ) : (
                <Icons type="bolt" size={27} />
              )
            }
            isActive={activeButton === "One Click"}
            onClick={() => handleButtonClick("One Click")}
            themeColor={theme.colors.primary}
          />
        </div>

        <div
          className="flex-1 rounded-lg px-3 py-0 transition-all duration-200 flex items-center justify-center min-h-[43px]"
          style={getActiveTabStyle(activeButton === "Credit Card")}
        >
          <MoreOptionTabButton
            title="Card"
            icon={
              activeButton === "Credit Card" ? (
                <Icons type="cardActive" size={27} color={theme.colors.primary} />
              ) : (
                <Icons type="addNewCard" size={27} />
              )
            }
            isActive={activeButton === "Credit Card"}
            onClick={() => handleButtonClick("Credit Card")}
            themeColor={theme.colors.primary}
          />
        </div>

        <div
          className="flex-1 rounded-lg px-3 py-0 transition-all duration-200 flex items-center justify-center min-h-[43px]"
          style={getActiveTabStyle(activeButton === "More Options")}
        >
          <MoreOptionTabButton
            icon={
              activeButton === "More Options" ? (
                <Icons type="moreOptionActive" size={44} color={theme.colors.primary} />
              ) : (
                <Icons type="moreOption" size={44} />
              )
            }
            title="More Options"
            isActive={activeButton === "More Options"}
            onClick={() => handleButtonClick("More Options")}
            themeColor={theme.colors.primary}
          />
        </div>
      </div>}

      {(activeButton === "Credit Card" || activeButton === "One Click") && (
        <PaymentMethods
          activeButton={activeButton as "Credit Card" | "One Click"}
          savedCards={savedCards}
          email={email}
          showNav={showNav}
          setShowNav={setShowNav}
          setEmail={setEmail}
          emailError={emailError}
          setEmailError={setEmailError}
          onConfirm={onConfirm}
          showApplePay={showApplePay}
          showGooglePay={showGooglePay}
          isPaymentMethodsLoading={false}
          clientSecret={clientSecret}
          sessionId={sessionId}
          gameData={gameData}
          isSkeletonPhase={isSkeletonPhase}
          hasExpressLoadedOnce={hasExpressLoadedOnce}
          onExpressLoaded={onExpressLoaded}
          isExpressReinitializing={isExpressReinitializing}
          onExpressReinitialized={onExpressReinitialized}
          paypalEnabled={paypalEnabled}
          optimisticGpay={optimisticGpay}
        />
      )}
      {activeButton === "More Options" && (
        <MoreOptionErrorBoundary>
          <Suspense fallback={
            <div className="px-5">
              <Skeleton className="h-[48px] w-full" />
              <div className="text-center text-xs text-gray-400 mt-2">Loading payment options...</div>
            </div>
          }>
            <MoreOption 
              onConfirm={onConfirm} 
              paymentMethodDefinitions={paymentMethodDefinitions}
              gameData={gameData}
              sessionId={sessionId}
            />
          </Suspense>
        </MoreOptionErrorBoundary>
      )}
    </section>
  );
};

export default React.memo(PaymentMethodsList);
